import type { GetStaticPaths } from 'next'
import { slugs } from '@/constants'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { Downloadable, getPressPageContentByProject, PressRelease, Project, TeamMember } from '@/services/PressService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { EmbeddedVideo } from '@/types/codegen-contentful'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PressView } from '@/views/PressView'

interface PressProjectPageProps {
  locale: string
  downloadables: Downloadable[]
  pressReleases: PressRelease[]
  project: Project
  projectMeta: ProjectMeta
  teamMembers: TeamMember[]
  videos: EmbeddedVideo[]
}

const PressProjectPage: NextPageWithLayout<PressProjectPageProps> = ({
  locale,
  downloadables,
  pressReleases,
  project,
  projectMeta,
  teamMembers,
  videos,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale })

  const url = useCanonicalUrl(`${paths.press.index}/${project.slug}`)
  const title = `${project.name} Press | Angel Studios`
  const description = `${project.name} Press Room`

  return (
    <>
      <Seo {...meta} canonical={url} title={title} description={description} />
      <PressView
        downloadables={downloadables}
        pressReleases={pressReleases}
        project={project}
        teamMembers={teamMembers}
        videos={videos}
      />
    </>
  )
}

PressProjectPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/press/[project].tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = params?.project as string
    const { downloadables, pressReleases, project, teamMembers, videos } = await getPressPageContentByProject(
      preview,
      projectSlug,
      locale,
    )

    if (projectSlug === slugs.truthAndTreasonSeries || projectSlug === slugs.truthAndConviction) {
      return {
        redirect: {
          destination: paths.press.truthAndTreason,
          permanent: false,
        },
      }
    }

    if (!project.hasPressPage) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    const projectMeta = await getProjectAbout({ slug: params?.project as string }, federationClient, {
      locale,
      preview,
    })
    if (!projectMeta) {
      return next404()
    }

    return {
      props: {
        locale,
        downloadables,
        pressReleases,
        project,
        projectMeta,
        teamMembers,
        videos,
        ...(await loadTranslations(locale, ['common', 'account', 'watch'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default PressProjectPage
